import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { motion } from 'framer-motion';
import { routesData } from '../data/routesData';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';

const generateBookingRef = () => {
  const now = Date.now().toString(36).toUpperCase();
  const rand = Math.floor(Math.random() * 1000000).toString(36).toUpperCase();
  return `BT-${now}-${rand}`;
};

const SeatMap = ({ selectedSeats, onSeatSelect, occupiedSeats = [], disabled = false }) => {
  const seats = useMemo(() => Array.from({ length: 55 }, (_, i) => i + 1), []);

  return (
    <div className="grid grid-cols-10 gap-3 p-6 bg-neutral-50 rounded-2xl">
      {seats.map((seatNum) => {
        const isOccupied = occupiedSeats.includes(seatNum);
        const isSelected = selectedSeats.includes(seatNum);

        const baseClasses =
          'w-10 h-10 flex items-center justify-center rounded-xl text-sm font-bold border transition';
        const stateClasses = disabled
          ? 'bg-neutral-200 text-neutral-500 border-neutral-300 cursor-not-allowed opacity-50'
          : isOccupied
          ? 'bg-neutral-300 text-neutral-500 border-neutral-400 cursor-not-allowed line-through'
          : isSelected
          ? 'bg-primary-600 text-white border-primary-700 shadow-lg scale-105'
          : 'bg-white text-neutral-800 border-neutral-300 hover:border-primary-500 hover:shadow cursor-pointer';

        return (
          <button
            key={seatNum}
            type="button"
            disabled={disabled || isOccupied}
            onClick={() => !disabled && onSeatSelect(seatNum)}
            className={`${baseClasses} ${stateClasses}`}
          >
            {seatNum}
          </button>
        );
      })}
    </div>
  );
};

export default function Booking() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const routesWithPrice = useMemo(() => 
    routesData.filter(route => route.price > 0), 
    []
  );

  const [filter, setFilter] = useState({
    from: searchParams.get('from') || '',
    to: searchParams.get('to') || '',
    date: searchParams.get('date') || '',
    tour: 'matin',
    bus: null,
  });

  const [selectedTrip, setSelectedTrip] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [occupiedSeats, setOccupiedSeats] = useState([]);
  const [manualSeat, setManualSeat] = useState('');

  const [passengerInfo, setPassengerInfo] = useState({
    firstName: '',
    lastName: '',
    phone: '',
  });

  const [paymentMethod, setPaymentMethod] = useState('airtel_money');
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [loadingSeats, setLoadingSeats] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingRef, setBookingRef] = useState(null);

  const departureCities = useMemo(() => {
    const set = new Set(routesWithPrice.map(r => r.departure));
    return Array.from(set).sort();
  }, [routesWithPrice]);

  const arrivalCities = useMemo(() => {
    const filtered = filter.from
      ? routesWithPrice.filter(r => r.departure === filter.from)
      : routesWithPrice;
    const set = new Set(filtered.map(r => r.arrival));
    return Array.from(set).sort();
  }, [filter.from, routesWithPrice]);

  useEffect(() => {
    const loadTrips = async () => {
      if (!filter.from || !filter.to || !filter.date) {
        setSelectedTrip(null);
        return;
      }

      setLoadingTrips(true);
      const matches = routesWithPrice.filter(
        (r) => r.departure === filter.from && r.arrival === filter.to
      );

      if (matches.length > 0) {
        setSelectedTrip(matches[0]);
      } else {
        setSelectedTrip(null);
      }
      setLoadingTrips(false);
    };

    loadTrips();
  }, [filter, routesWithPrice]);

  useEffect(() => {
    const fetchOccupiedSeats = async () => {
      if (!selectedTrip || !filter.date || !filter.bus) {
        setOccupiedSeats([]);
        return;
      }

      setLoadingSeats(true);
      try {
        const reservationsRef = collection(db, 'reservations');
        const q = query(
          reservationsRef,
          where('tripId', '==', selectedTrip.id),
          where('travelDate', '==', filter.date),
          where('tour', '==', filter.tour),
          where('busNumber', '==', filter.bus)
        );
        const snapshot = await getDocs(q);

        const seatsSet = new Set();
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.tour === 'matin' && data.seats) {
            (data.seats || []).forEach((s) => seatsSet.add(s));
          } else if (data.tour === 'soir' && data.assignedSeat) {
            seatsSet.add(data.assignedSeat);
          }
        });

        setOccupiedSeats(Array.from(seatsSet));
      } catch (err) {
        console.error('Erreur chargement sièges:', err);
      } finally {
        setLoadingSeats(false);
      }
    };

    fetchOccupiedSeats();
  }, [selectedTrip, filter.date, filter.tour, filter.bus]);

  useEffect(() => {
    if (!bookingSuccess) return;
    const timer = setTimeout(() => {
      navigate('/');
    }, 60000);
    return () => clearTimeout(timer);
  }, [bookingSuccess, navigate]);

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({ ...prev, [field]: value }));
    if (field === 'from' || field === 'to') {
      setSelectedTrip(null);
      setSelectedSeats([]);
      setManualSeat('');
      setFilter(prev => ({ ...prev, bus: null }));
    }
  };

  const handleSeatSelect = (seatNum) => {
    if (filter.tour === 'matin') {
      setSelectedSeats((prev) =>
        prev.includes(seatNum)
          ? prev.filter((s) => s !== seatNum)
          : [...prev, seatNum]
      );
    } else {
      setManualSeat(seatNum.toString());
    }
  };

  const handlePassengerChange = (field, value) => {
    setPassengerInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleBooking = async (e) => {
    e.preventDefault();

    if (!selectedTrip) {
      alert(t('no_trip_selected'));
      return;
    }

    if (!filter.bus) {
      alert('Veuillez sélectionner un bus');
      return;
    }

    if (filter.tour === 'matin' && selectedSeats.length === 0) {
      alert(t('select_at_least_one_seat'));
      return;
    }

    if (filter.tour === 'soir' && !manualSeat) {
      alert(t('select_at_least_one_seat'));
      return;
    }

    if (!passengerInfo.firstName || !passengerInfo.lastName || !passengerInfo.phone) {
      alert(t('fill_passenger_info'));
      return;
    }

    const ref = generateBookingRef();
    setBookingRef(ref);

    const reservation = {
      bookingRef: ref,
      tripId: selectedTrip.id,
      tripCode: selectedTrip.code,
      departure: selectedTrip.departure,
      arrival: selectedTrip.arrival,
      travelDate: filter.date,
      tour: filter.tour,
      busNumber: filter.bus,
      passenger: passengerInfo,
      seats: filter.tour === 'matin' ? selectedSeats : [],
      assignedSeat: filter.tour === 'soir' ? parseInt(manualSeat) : null,
      unitPrice: selectedTrip.price,
      totalAmount: selectedTrip.price * (filter.tour === 'matin' ? selectedSeats.length : 1),
      status: 'pending',
      paymentMethod,
      createdAt: new Date().toISOString(),
      userId: null,
    };

    try {
      setSubmitting(true);
      await addDoc(collection(db, 'reservations'), reservation);
      setBookingSuccess(true);
    } catch (err) {
      console.error('Erreur réservation:', err);
      alert(t('reservation_error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadTicket = async () => {
    if (!bookingRef || !selectedTrip) return;

    const qrUrl = `${window.location.origin}/admin?bookingRef=${encodeURIComponent(bookingRef)}&phone=${encodeURIComponent(passengerInfo.phone)}`;
    let qrDataUrl;
    try {
      qrDataUrl = await QRCode.toDataURL(qrUrl);
    } catch (err) {
      console.error('Erreur QRCode:', err);
    }

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [80, 150]
    });

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('EXPRESS ABOU HAMAMA', 40, 10, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('BILLET DE BUS', 40, 15, { align: 'center' });
    
    if (qrDataUrl) {
      doc.addImage(qrDataUrl, 'PNG', 25, 20, 30, 30);
    }
    
    doc.setFontSize(8);
    doc.text(`Réf: ${bookingRef}`, 5, 55);
    doc.text(`Trajet: ${selectedTrip.departure} → ${selectedTrip.arrival}`, 5, 60);
    doc.text(`Date: ${filter.date}`, 5, 65);
    doc.text(`Bus: ${filter.bus}`, 5, 70);
    doc.text(`Tour: ${filter.tour}`, 5, 75);
    
    if (filter.tour === 'matin') {
      doc.text(`Sièges: ${selectedSeats.join(', ')}`, 5, 80);
    } else {
      doc.text(`Place: ${manualSeat}`, 5, 80);
    }
    
    doc.text(`Passager: ${passengerInfo.firstName} ${passengerInfo.lastName}`, 5, 85);
    doc.text(`Tél: ${passengerInfo.phone}`, 5, 90);
    
    doc.setFontSize(6);
    doc.text('Presentez ce billet à bord du bus', 40, 140, { align: 'center' });
    doc.text('www.expressabouhamama.td', 40, 145, { align: 'center' });

    doc.save(`billet-${bookingRef}.pdf`);
  };

  if (bookingSuccess) {
    return (
      <div className={`${i18n.language === 'ar' ? 'rtl' : ''} container mx-auto px-4 py-16 max-w-3xl`}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-green-100 text-green-800 p-8 rounded-2xl text-center shadow-xl"
        >
          <h2 className="text-2xl font-bold mb-4">{t('booking_success')}</h2>
          <p className="mb-2">{t('booking_success_message')}</p>
          {bookingRef && (
            <p className="mb-2 font-mono">
              <span className="font-semibold">{t('booking_ref')} </span>{bookingRef}
            </p>
          )}
          <p className="text-sm text-green-900 mb-4">{t('redirect_home')}</p>
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={handleDownloadTicket}
              className="px-6 py-3 rounded-xl bg-primary-600 text-white font-semibold shadow-lg hover:bg-primary-700 transition"
            >
              {t('download_ticket')}
            </button>
            <button onClick={() => navigator.clipboard?.writeText?.(bookingRef || '')} className="text-sm underline text-primary-700">
              {t('copy_ref')}
            </button>
            <button onClick={() => navigate('/')} className="text-xs text-gray-600 underline mt-2">
              {t('back_home')}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={`${i18n.language === 'ar' ? 'rtl' : ''} container mx-auto px-4 py-8 max-w-6xl`}>
      {/* FILTRE DE SÉLECTION */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white p-6 rounded-2xl shadow-lg border border-neutral-100 mb-8"
      >
        <h2 className="text-2xl font-bold mb-4">{t('search_trip')}</h2>
        <form className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {/* DEPART */}
          <div>
            <label className="block text-sm font-semibold text-neutral-900 mb-2">{t('search_departure')}</label>
            <select
              className="w-full p-3 rounded-xl border border-neutral-200 focus:border-primary-500 outline-none"
              value={filter.from}
              onChange={(e) => handleFilterChange('from', e.target.value)}
              required
            >
              <option value="">{t('search_departure')}</option>
              {departureCities.map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          {/* ARRIVEE */}
          <div>
            <label className="block text-sm font-semibold text-neutral-900 mb-2">{t('search_arrival')}</label>
            <select
              className="w-full p-3 rounded-xl border border-neutral-200 focus:border-primary-500 outline-none"
              value={filter.to}
              onChange={(e) => handleFilterChange('to', e.target.value)}
              required
              disabled={!filter.from}
            >
              <option value="">
                {filter.from ? t('search_arrival') : t('choose_departure_first')}
              </option>
              {arrivalCities.map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          {/* DATE */}
          <div>
            <label className="block text-sm font-semibold text-neutral-900 mb-2">{t('search_date')}</label>
            <input
              type="date"
              className="w-full p-3 rounded-xl border border-neutral-200 focus:border-primary-500 outline-none"
              value={filter.date}
              onChange={(e) => handleFilterChange('date', e.target.value)}
              required
            />
          </div>

          {/* TOUR */}
          <div>
            <label className="block text-sm font-semibold text-neutral-900 mb-2">Tour</label>
            <select
              className="w-full p-3 rounded-xl border border-neutral-200 focus:border-primary-500 outline-none"
              value={filter.tour}
              onChange={(e) => {
                handleFilterChange('tour', e.target.value);
                setSelectedSeats([]);
                setManualSeat('');
              }}
              required
            >
              <option value="matin">🌅 Matin (sièges attribués)</option>
              <option value="soir">🌆 Soir (attribution manuelle)</option>
            </select>
          </div>

          {/* BUS - SIMPLIFIÉ COMME LE TOUR */}
          <div>
            <label className="block text-sm font-semibold text-neutral-900 mb-2">Bus</label>
            <select
              className="w-full p-3 rounded-xl border border-neutral-200 focus:border-primary-500 outline-none"
              value={filter.bus || ''}
              onChange={(e) => handleFilterChange('bus', e.target.value ? parseInt(e.target.value) : null)}
              required
              disabled={!selectedTrip}
            >
              <option value="" disabled={!selectedTrip}>
                {selectedTrip ? 'Sélectionnez un bus' : 'Choisissez d\'abord le trajet'}
              </option>
              {selectedTrip && (
                <>
                  <option value="1">🚌 Bus 1</option>
                  <option value="2">🚍 Bus 2</option>
                  <option value="3">🚐 Bus 3</option>
                </>
              )}
            </select>
          </div>

          {/* BOUTON RECHERCHE */}
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => {
                if (!filter.from || !filter.to || !filter.date || !filter.bus) {
                  alert(t('please_fill_all_fields'));
                  return;
                }
                setFilter({ ...filter });
              }}
              className="w-full bg-primary-600 text-white font-semibold py-3 rounded-xl hover:bg-primary-700 transition"
            >
              {t('search_button')}
            </button>
          </div>
        </form>

        {/* Messages d'information */}
        {filter.tour === 'soir' && (
          <div className="mt-4 p-3 bg-yellow-100 border border-yellow-300 rounded-xl">
            <p className="text-sm text-yellow-800">
              <strong>ℹ️ Tour du soir :</strong> Les places seront attribuées par l'administrateur selon l'ordre d'arrivée.
            </p>
          </div>
        )}
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-4xl font-display font-bold text-neutral-900 mb-8 text-center"
      >
        {t('booking_title')}
      </motion.h1>

      {/* SÉLECTION DU TRAJET */}
      <section className="mb-10">
        <h2 className="text-2xl font-bold mb-4">{t('select_trip')}</h2>

        {loadingTrips ? (
          <div className="text-center text-gray-500">{t('loading')}</div>
        ) : !selectedTrip ? (
          <div className="text-center text-red-500">
            {t('no_routes_found')}
            <button 
              onClick={() => {
                setFilter({ from: '', to: '', date: '', tour: 'matin', bus: null });
                setSelectedTrip(null);
              }}
              className="text-primary-600 underline ml-2"
            >
              {t('reset')}
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            <motion.div
              key={selectedTrip.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -4 }}
              className="text-left p-4 rounded-2xl border shadow-sm bg-green-50 border-green-500"
            >
              <div className="flex justify-between items-center mb-2">
                <div>
                  <p className="font-bold text-lg">{selectedTrip.departure} → {selectedTrip.arrival}</p>
                  <p className="text-sm text-gray-500">
                    {filter.date} - {filter.tour === 'matin' ? '🌅 Matin' : '🌆 Soir'} - Bus {filter.bus}
                  </p>
                </div>
                <p className="text-xl font-bold text-primary-600">
                  {selectedTrip.price.toLocaleString()} XAF
                </p>
              </div>
            </motion.div>
          </div>
        )}
      </section>

      {/* PLAN DES SIÈGES ET FORMULAIRE */}
      {selectedTrip && filter.bus && (
        <section className="grid md:grid-cols-3 gap-8 items-start">
          <div className="md:col-span-2">
            <h2 className="text-2xl font-bold mb-4">
              {filter.tour === 'matin' ? t('select_seats') : '🌆 Attribuer une place (soir)'}
            </h2>

            {loadingSeats ? (
              <p className="text-gray-500 mb-4">{t('loading')}</p>
            ) : (
              <p className="text-gray-500 mb-4">
                {filter.tour === 'matin' 
                  ? `${t('seats_occupied')}: ${occupiedSeats.length} • ${t('seats_selected')}: ${selectedSeats.length}`
                  : `Places occupées : ${occupiedSeats.length}`
                }
              </p>
            )}

            {filter.tour === 'matin' ? (
              <SeatMap
                selectedSeats={selectedSeats}
                onSeatSelect={handleSeatSelect}
                occupiedSeats={occupiedSeats}
              />
            ) : (
              <div className="p-6 bg-yellow-50 border border-yellow-300 rounded-2xl">
                <p className="text-sm text-yellow-800 mb-4">
                  <strong>ℹ️ Mode soir :</strong> Cliquez sur une place libre pour la réserver.
                </p>
                <SeatMap
                  selectedSeats={manualSeat ? [parseInt(manualSeat)] : []}
                  onSeatSelect={handleSeatSelect}
                  occupiedSeats={occupiedSeats}
                  disabled={false}
                />
              </div>
            )}
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-lg border border-neutral-100">
            <h2 className="text-2xl font-bold mb-4">{t('passenger_information')}</h2>

            <form onSubmit={handleBooking} className="space-y-4">
              {/* Prénom */}
              <div>
                <label className="block text-sm font-semibold mb-1">{t('first_name')}</label>
                <input
                  type="text"
                  className="w-full p-3 rounded-xl border border-neutral-200 focus:border-primary-500 outline-none"
                  value={passengerInfo.firstName}
                  onChange={(e) => handlePassengerChange('firstName', e.target.value)}
                  required
                />
              </div>

              {/* Nom */}
              <div>
                <label className="block text-sm font-semibold mb-1">{t('last_name')}</label>
                <input
                  type="text"
                  className="w-full p-3 rounded-xl border border-neutral-200 focus:border-primary-500 outline-none"
                  value={passengerInfo.lastName}
                  onChange={(e) => handlePassengerChange('lastName', e.target.value)}
                  required
                />
              </div>

              {/* Téléphone */}
              <div>
                <label className="block text-sm font-semibold mb-1">{t('phone')}</label>
                <input
                  type="tel"
                  className="w-full p-3 rounded-xl border border-neutral-200 focus:border-primary-500 outline-none"
                  value={passengerInfo.phone}
                  onChange={(e) => handlePassengerChange('phone', e.target.value)}
                  required
                />
              </div>

              {/* Mode de paiement */}
              <div>
                <p className="block text-sm font-semibold mb-2">{t('payment_method')}</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="radio" value="airtel_money" checked={paymentMethod === 'airtel_money'} onChange={(e) => setPaymentMethod(e.target.value)} />
                    <span>{t('airtel_money')}</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="radio" value="moov_money" checked={paymentMethod === 'moov_money'} onChange={(e) => setPaymentMethod(e.target.value)} />
                    <span>{t('moov_money')}</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="radio" value="cash" checked={paymentMethod === 'cash'} onChange={(e) => setPaymentMethod(e.target.value)} />
                    <span>{t('cash_agency')}</span>
                  </label>
                </div>
              </div>

              {/* Total */}
              <div className="pt-4 border-t border-neutral-200 mt-4">
                <p className="text-sm text-gray-600 mb-2">
                  {t('total_to_pay')} ({filter.tour === 'matin' ? selectedSeats.length : 1} {t('seat')}{filter.tour === 'matin' && selectedSeats.length > 1 ? 's' : ''}) :
                </p>
                <p className="text-2xl font-bold text-primary-600">
                  {(selectedTrip.price * (filter.tour === 'matin' ? selectedSeats.length : 1) || 0).toLocaleString()} XAF
                </p>
              </div>
            </form>

            {/* Bouton de réservation */}
            <button
              type="submit"
              onClick={handleBooking}
              disabled={(filter.tour === 'matin' && selectedSeats.length === 0) || 
                       (filter.tour === 'soir' && !manualSeat) || 
                       submitting ||
                       !filter.bus}
              className="w-full bg-gradient-to-r from-primary-600 to-accent-500 text-white font-bold py-3 rounded-xl mt-4 shadow-lg hover:from-primary-700 hover:to-accent-600 transition disabled:opacity-50"
            >
              {filter.tour === 'matin' && selectedSeats.length === 0
                ? t('select_seats_first')
                : filter.tour === 'soir' && !manualSeat
                ? 'Sélectionnez une place'
                : !filter.bus
                ? 'Sélectionnez un bus'
                : submitting
                ? t('booking_in_progress')
                : `${t('confirm_booking')} - ${(selectedTrip.price * (filter.tour === 'matin' ? selectedSeats.length : 1)).toLocaleString()} XAF`}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}